import {
    Button,
    Message,
    ModalBody,
    ModalDialog,
    ModalFooter,
    ModalHeader,
    ModalOverlay
} from '@flos/react-ui';
import { useHippoText } from '../../hooks/useHippoText';
import useOpcJob from '../../hooks/useOpcJob';
import usePageNavigation from '../../hooks/usePageNavigation';
import { useAsyncThrow } from '../../hooks/useAsyncThrow';
import { useRecoilState } from 'recoil';
import { isOpcFetchingAtom } from '../../recoil/atoms/isOpcFetchingAtom';
import { opcInfoAtom } from '../../recoil/atoms/opcInfoAtom';
import type { OPCChangeType } from '../../recoil/types/opcTypes';

const ExistingShoppingBasketModal = ({
    existingBasket,
    showModal,
    closeModal
}: {
    existingBasket: {
        jobNumber: string;
        sessionId: string;
    };
    showModal: boolean;
    closeModal: (isOpenOPCModal: boolean) => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { createNewPolicyChange } = useOpcJob();
    const { goBackToDetailsPage, goToShoppingBasket } = usePageNavigation();
    const { asyncThrow } = useAsyncThrow();
    const [opcInfo, setOpcInfo] = useRecoilState(opcInfoAtom);
    const [isOpcFetching, setIsOpcFetching] = useRecoilState(isOpcFetchingAtom);

    const labels = {
        headerModal: getHippoText('DSS-OPC-EXISTING-BASKET-MODAL'),
        description: getHippoText('DSS-OPC-EXISTING-BASKET-DESCRIPTION'),
        warningText: getHippoText('DSS-OPC-EXISTING-BASKET-WARNING'),
        cancelBtnText: getHippoText('DSS-OPC-EXISTING-BASKET-GO-TO-BASKET'),
        submitBtnText: getHippoText('DSS-OPC-EXISTING-BASKET-CONTINUE')
    };

    const continueWithNewChange = (): void => {
        // There always a new policy change with renewal date created after remove button clicked
        // No need to create again here
        if (opcInfo.adjustAction === 'Remove') {
            closeModal(true);
            return;
        }
        setIsOpcFetching(true);
        void createNewPolicyChange()
            .then(() => {
                closeModal(true);
            })
            .catch((e) => {
                asyncThrow(e);
            });
    };

    return (
        <ModalOverlay isOpen={showModal}>
            <ModalDialog
                closeIconHandler={() => {
                    if (isOpcFetching) return;
                    goBackToDetailsPage();
                }}
            >
                <ModalHeader title={labels.headerModal}></ModalHeader>
                <ModalBody className="u-flex-box u-flex-box--col u-spacing-stack-m">
                    {labels.description}
                    <Message theme={'warning'} title={<b>Bemærk:</b>}>
                        {labels.warningText}
                    </Message>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={continueWithNewChange} disabled={isOpcFetching}>
                        {labels.submitBtnText}
                    </Button>
                    <Button
                        theme="secondary"
                        disabled={isOpcFetching}
                        onClick={() => {
                            setOpcInfo((preInfo) => ({ ...preInfo, tilpas: '' as OPCChangeType }));
                            goToShoppingBasket(true, existingBasket.jobNumber);
                            closeModal(false);
                        }}
                    >
                        {labels.cancelBtnText}
                    </Button>
                </ModalFooter>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default ExistingShoppingBasketModal;
